import {Storage as CapacitorStore} from '@capacitor/storage';
import {Capacitor} from '@capacitor/core';
import {IndexedDbStorage} from './IndexedDbStorage';

// On web, route through IndexedDB to avoid the ~5 MB localStorage total quota.
// On native (Android/iOS), keep Capacitor Storage which uses NativeStorage.
const Store = (typeof indexedDB !== 'undefined' && !Capacitor.isNativePlatform())
    ? IndexedDbStorage
    : CapacitorStore;

export const ZRA_DETAILS = 'zra_details'
export const DEFAULT_STORE = 'default_store'
export const ALL_STORES = 'all_stores'
export const FRANCHISE_SETTINGS = 'franchise_settings'

export const ADMIN = 'admin'
export const CATEGORIES = 'categories'
export const AllUsers = 'all_users'
export const AllSecurities = 'securities'
export const CurrentUser = 'current_user'
export const STOCK = 'stock'
export const PAYMENT_METHODS = 'payment_methods'
export const STOCK_SETTING = 'stock_setting'

export const HAS543 = 'has543'
export const HASKAZANG = 'hasKazang'
export const KAZANGSESSION = 'kazangSession'
export const KAZANGPRODUCTS = 'kazangProductList'

export const FLOAT = 'float'
export const DEVICE_SETTINGS = 'device_settings'
export const SAVED_TABLES = 'saved_tables'

export const BASE_CURRENCY = 'base_currency'

export const USERS = 'users'
export const SUPPORT_AGENT = 'support_agent'

const notEmpty = (val) => {
    if (!val) return false
    return val !== ''
}
const encryptAdmin = (val) => {
    // return CryptoJS.AES.encrypt(val, 'Admin-User-Hello').toString()
    return val;
}
const decryptAdmin = (val) => {
    // return CryptoJS.AES.decrypt(val, 'Admin-User-Hello').toString(CryptoJS.enc.Utf8)
    return JSON.parse(val);
}

/**
 * Safely reads a value from Capacitor Storage.
 * Returns null if the key is missing, value is empty, or read throws.
 */
const safeGet = async (key) => {
    try {
        const { value } = await Store.get({ key });
        return value ?? null;
    } catch (e) {
        console.warn(`Storage.get failed for "${key}"`, e);
        return null;
    }
};

/**
 * Safely writes a value to Capacitor Storage.
 * Returns true on success, false if quota exceeded or write throws.
 */
const safeSet = async (key, value) => {
    try {
        await Store.set({ key, value });
        return true;
    } catch (e) {
        console.warn(`Storage.set failed for "${key}"`, e);
        return false;
    }
};

/**
 * Builds the per-PLU storage key used by setStockSettings/getStockSetting.
 * Replaces the legacy single-blob `stock_setting` key which hit the per-key quota.
 */
const stockSettingKey = (plu) => `${STOCK_SETTING}_${plu}`;

/**
 * Safely parses a JSON string. Returns null on null/empty input or parse errors.
 */
const safeParse = (raw) => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const Storage = {
    /**
     * Persists the authenticated admin user. Read by getAdminUser.
     * Routes the write through safeSet; failures (e.g. quota) are logged and swallowed.
     * @param {object} user Admin user payload (will be JSON.stringify'd, then encryptAdmin wrapped).
     */
    setAdminUser: async (user) => {
        await safeSet(ADMIN, encryptAdmin(JSON.stringify(user)));
    },
    /**
     * Reads the admin user from storage. Returns null if missing or read fails.
     * Safe: storage read is wrapped; decryptAdmin is only called when a value exists.
     */
    getAdminUser: async () => {
        const value = await safeGet(ADMIN);
        return value ? decryptAdmin(value) : null;
    },

    /**
     * Writes the full list of registered stores for the franchise group.
     * Safe: routed through safeSet; quota errors are silently swallowed.
     */
    setAllStores : async (payload) => {
        await safeSet(ALL_STORES, JSON.stringify(payload));
    },

    /**
     * Reads the registered stores list. Returns null if missing, read fails, or JSON is corrupt.
     */
    getAllStores : async () => {
        const value = await safeGet(ALL_STORES);
        return safeParse(value);
    },

    /**
     * Persists franchise-level settings (tax rates, group flags, etc.).
     */
    setFranchiseSettings : async (payload) => {
        await safeSet(FRANCHISE_SETTINGS, JSON.stringify(payload));
    },

    /**
     * Reads franchise settings. Returns null if missing or parse fails.
     */
    getFranchiseSettings : async () => {
        const value = await safeGet(FRANCHISE_SETTINGS);
        return safeParse(value);
    },

    /**
     * Writes ZRA (tax authority) details for the current store.
     */
    setZRADetails: async (payload) => {
        await safeSet(ZRA_DETAILS, JSON.stringify(payload));
    },

    /**
     * Writes the list of stores registered to the franchise group.
     * Alias of setAllStores; both target the ALL_STORES key.
     */
    setRegisteredStores: async (stores) => {
        await safeSet(ALL_STORES, JSON.stringify(stores));
    },

    /**
     * Writes the category tree used by the category bar / sub-department filters.
     */
    setCategories: async (categories) => {
        await safeSet(CATEGORIES, JSON.stringify(categories));
    },
    /**
     * Reads the category tree. Returns null if missing or parse fails.
     */
    getCategories: async () => {
        const value = await safeGet(CATEGORIES);
        return safeParse(value);
    },

    /**
     * Writes the boolean flag indicating whether the device uses the 543 mobile-money integration.
     */
    setHas543: async (has543) => {
        await safeSet(HAS543, JSON.stringify(has543));
    },
    /**
     * Reads the has543 flag. Returns null if missing or parse fails.
     */
    getHas543: async () => {
        const value = await safeGet(HAS543);
        return safeParse(value);
    },

    /**
     * Writes the boolean flag indicating whether the device uses the Kazang integration.
     */
    setHasKazang: async (payload) => {
        await safeSet(HASKAZANG, JSON.stringify(payload));
    },

    /**
     * Reads the hasKazang flag. Returns null if missing or parse fails.
     */
    getHasKazang: async () => {
        const value = await safeGet(HASKAZANG);
        return safeParse(value);
    },

    /**
     * Reads the persisted ZRA details. Returns null if missing or parse fails.
     */
    getZRADetails: async () => {
        const value = await safeGet(ZRA_DETAILS);
        return safeParse(value);
    },
    /**
     * Persists the user's "default" store selection (used after login).
     */
    setDefaultStore: async (defaultStore) => {
        await safeSet(DEFAULT_STORE, JSON.stringify(defaultStore));
    },
    /**
     * Reads the default store. Returns null if missing or parse fails.
     */
    getDefaultStore: async () => {
        const value = await safeGet(DEFAULT_STORE);
        return safeParse(value);
    },
    /**
     * Writes the cached list of all users for this device.
     */
    setAllUsers: async (users) => {
        await safeSet(AllUsers, JSON.stringify(users));
    },
    /**
     * Writes the cached list of security (PIN) records for users on this device.
     */
    setAllSecurities: async (users) => {
        await safeSet(AllSecurities, JSON.stringify(users));
    },
    /**
     * Reads the cached security/PIN records. Returns null if missing or parse fails.
     */
    getAllSecurities: async () => {
        const value = await safeGet(AllSecurities);
        return safeParse(value);
    },
    /**
     * Reads the cached user list. Returns null if missing or parse fails.
     */
    getAllUsers: async () => {
        const value = await safeGet(AllUsers);
        return safeParse(value);
    },
    /**
     * Writes the currently-logged-in user (used for permission checks and audit).
     */
    setCurrentUser: async (currentUser) => {
        await safeSet(CurrentUser, JSON.stringify(currentUser));
    },
    /**
     * Reads the currently-logged-in user. Returns null if missing or parse fails.
     */
    getCurrentUser: async () => {
        const value = await safeGet(CurrentUser);
        return safeParse(value);
    },
    /**
     * Persists the full stock catalog for this store. Routed via safeSet (silent on quota).
     */
    setStock: async (stock) => {
        await safeSet(STOCK, JSON.stringify(stock));
    },
    /**
     * Reads the full stock catalog. Returns null if missing or parse fails.
     * Callers MUST null-check before using `.find`/`.filter`.
     */
    getAllStock: async () => {
        const value = await safeGet(STOCK);
        return safeParse(value);
    },
    /**
     * Returns a single stock item by PLU, or undefined when the catalog is missing/empty.
     * @param {string} plu PLU code to look up.
     */
    getByPlu: async (plu) => {
        const value = await safeGet(STOCK);
        const stock = safeParse(value) ?? [];
        return stock.find(item => item.PLU === plu);
    },
    /**
     * Returns the subset of stock items flagged as FastLine (POS quick-pick buttons).
     * Returns an empty array when the catalog is missing/empty.
     */
    getFastLine: async () => {
        const value = await safeGet(STOCK);
        const stock = safeParse(value) ?? [];
        return stock.filter(sc => sc.FastLine === 1);
    },
    searchStockDesc: async (query) => {
        if (notEmpty(query.category)) {
            return (await Storage.searchStock(query))
                .filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                    || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                    || s.barcode.toLowerCase().includes(query.description.toLowerCase())
                ).sort((a, b) => {
                    return a.Description < b.Description;
                })
        }
        return (await Storage.getAllStock())
            .filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                || s.barcode.toLowerCase().includes(query.description.toLowerCase())
            ).sort((a, b) => {
                return a.Description < b.Description;
            })

    },
    /**
     * Filters the cached stock catalog by category / sub-category / sub2-category / description.
     * Safe: missing catalog returns [] (not null) so callers can chain .filter/.sort safely.
     * @param {{category:string,subCategory:string,sub2Category:string,description?:string}} query
     */
    searchStock: async (query) => {
        const value = await safeGet(STOCK);
        const stock = safeParse(value) ?? [];
        let result = [];
        if (query.category !== '' && query.subCategory !== '' && query.sub2Category !== '') {
            result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase())
                .filter(s2 => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase())
                .filter(s3 => s3.Subdep2.toLowerCase() === query.sub2Category.toLowerCase());
        } else if (query.category !== '' && query.subCategory !== '' && query.sub2Category === '') {
            result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase())
                .filter(s2 => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase())
        } else if (query.category !== '' && query.sub2Category === '' && query.subCategory === '') {
            result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase());
        }
        if (query.description) {
            return result.filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                || s.barcode.toLowerCase().includes(query.description.toLowerCase())
            )
        }
        return result.sort((a, b) => {
            return a.Description < b.Description;
        });
    },
    /**
     * Replaces a single stock item (matched by PLU) inside the cached catalog and re-persists.
     * Safe: missing catalog is treated as empty (no-op on the write).
     * @param {object} payload The replacement stock item (must include PLU).
     */
    editStockItem: async payload => {
        const value = await safeGet(STOCK);
        let stock = safeParse(value) ?? [];
        stock = stock.map(s => {
            if (s.PLU === payload.PLU) {
                return payload
            }
            return s;
        });
        await Storage.setStock(stock);
    },
    /**
     * Persists the cash float (opening drawer amount) for the current session.
     */
    setFloat: async (payload) => {
        await safeSet(FLOAT, JSON.stringify(payload));
    },
    /**
     * Reads the cash float. Returns null if missing or parse fails.
     */
    getFloat: async () => {
        const value = await safeGet(FLOAT);
        return safeParse(value);
    },
    /**
     * Writes device-scoped settings (PC id, flag prefix, separate-line toggle, etc.).
     */
    setDeviceSettings : async (payload) => {
        await safeSet(DEVICE_SETTINGS, JSON.stringify(payload));
    },

    /**
     * Reads device-scoped settings. Returns null if missing or parse fails.
     */
    getDeviceSettings: async () => {
        const value = await safeGet(DEVICE_SETTINGS);
        return safeParse(value);
    },
    /**
     * Reads the persisted "saved tables" (held sales). Returns null if missing or parse fails.
     */
    getAllTables : async () => {
        const value = await safeGet(SAVED_TABLES);
        return safeParse(value);
    },

    /**
     * Replaces the entire saved-tables list (held sales) with `payload`.
     */
    setTables : async (payload) => {
        await safeSet(SAVED_TABLES, JSON.stringify(payload));
    },

    /**
     * Appends a new held-sale table to the saved-tables list.
     * Safe: missing list is treated as empty before push.
     * @param {object} payload New held-sale table (must include SaleNum).
     */
    createTable : async (payload) => {
        const value = await safeGet(SAVED_TABLES);
        let tables = safeParse(value) ?? [];
        tables.push(payload);
        await safeSet(SAVED_TABLES, JSON.stringify(tables));
    },

    /**
     * Edits an existing held-sale table (matched by SaleNum). Safe on missing list.
     * @param {object} payload Replacement table (must include SaleNum).
     */
    editTable : async (payload) => {
        const value = await safeGet(SAVED_TABLES);
        let tables = safeParse(value) ?? [];
        tables = tables.map(table => {
            if (table.SaleNum === payload.SaleNum) {
                return payload
            }
            return table;
        });
        await safeSet(SAVED_TABLES, JSON.stringify(tables));
    },

    /**
     * Removes a held-sale table by SaleNum. Safe on missing list.
     * @param {string|number} salenum SaleNum of the held sale to drop.
     */
    deleteTable : async (salenum) => {
        const value = await safeGet(SAVED_TABLES);
        let tables = safeParse(value) ?? [];
        tables = tables.filter(table => table.SaleNum !== salenum);
        await safeSet(SAVED_TABLES, JSON.stringify(tables));
    },

    clear: async () => Store.clear(),
    /**
     * Persists per-PLU stock settings using one Capacitor Storage key per PLU
     * (e.g. `stock_setting_<PLU>`). This replaces the old single-blob `stock_setting`
     * key which exceeded the per-key quota once the catalog grew.
     *
     * Each write is routed through safeSet; quota errors on individual entries are
     * swallowed (logged) so one oversize entry cannot block the rest of the batch.
     * Non-array input is a no-op. Items without a `plu` field are skipped.
     *
     * Idempotent: re-running this with the same data overwrites the same keys.
     *
     * @param {Array<{plu:string,...}>} stock Array of stock-setting objects.
     */
    setStockSettings: async (stock) => {
        if (!Array.isArray(stock)) return;
        await Promise.all(stock.map(async (s) => {
            if (!s || !s.plu) return;
            await safeSet(stockSettingKey(s.plu), JSON.stringify(s));
        }));
    },
    /**
     * Reads the per-PLU stock-setting entry for a given PLU.
     * Returns `undefined` when the PLU has no stored settings, the storage read fails,
     * or the persisted blob is not valid JSON. Callers MUST handle the `undefined` case.
     * @param {string} plu PLU to look up.
     * @returns {Promise<object|undefined>}
     */
    getStockSetting: async (plu) => {
        if (!plu) return undefined;
        const raw = await safeGet(stockSettingKey(plu));
        if (!raw) return undefined;
        try {
            return JSON.parse(raw);
        } catch {
            return undefined;
        }
    },

    /**
     * Migrates the legacy single-blob `stock_setting` key into per-PLU keys
     * (`stock_setting_<PLU>`), then deletes the legacy key.
     *
     * Idempotent by design: a no-op if the legacy key is already absent, and safe
     * to call on every boot until the old key is gone. Corrupt legacy blobs are
     * left in place so per-PLU reads fall through cleanly without losing data.
     */
    migrateStockSettings: async () => {
        const raw = await safeGet(STOCK_SETTING);
        if (!raw) return;
        try {
            const arr = JSON.parse(raw);
            if (!Array.isArray(arr)) return;
            await Promise.all(arr.map(async (s) => {
                if (s && s.plu) {
                    await safeSet(stockSettingKey(s.plu), JSON.stringify(s));
                }
            }));
            try { await Store.remove({ key: STOCK_SETTING }); } catch (e) { /* swallow */ }
        } catch (e) {
            // Corrupt legacy blob — leave it alone, per-PLU reads will fall through
            console.warn('migrateStockSettings: legacy blob unparseable', e);
        }
    },

    /**
     * Removes every `stock_setting_*` key from storage. Used by full-reset flows.
     * Each removal is individually try/catch'd so one failure cannot abort the batch.
     */
    clearAllStockSettings: async () => {
        const { keys } = await Store.keys();
        const targets = keys.filter(k => k.startsWith(`${STOCK_SETTING}_`));
        await Promise.all(targets.map(k => Store.remove({ key: k }).catch(() => {})));
    },

    /**
     * Persists the configured payment methods (cash, card, Kazang, etc.).
     */
    setPaymentMethods: async (data) => {
        await safeSet(PAYMENT_METHODS, JSON.stringify(data));
    },
    /**
     * Reads the configured payment methods. Returns null if missing or parse fails.
     */
    getPaymentMethods: async () => {
        const value = await safeGet(PAYMENT_METHODS);
        return safeParse(value);
    },

    /**
     * Persists the active Kazang session token / metadata.
     */
    setKazangSession: async (data) => {
        await safeSet(KAZANGSESSION, JSON.stringify(data));
    },
    /**
     * Reads the Kazang session. Returns null if missing or parse fails.
     */
    getKazangSession: async () => {
        const value = await safeGet(KAZANGSESSION);
        return safeParse(value);
    },

    /**
     * Persists the cached Kazang product list used for voucher purchase flows.
     */
    setKazangProductList: async (data) => {
        await safeSet(KAZANGPRODUCTS, JSON.stringify(data));
    },
    /**
     * Reads the cached Kazang product list. Returns null if missing or parse fails.
     */
    getKazangProductList: async () => {
        const value = await safeGet(KAZANGPRODUCTS);
        return safeParse(value);
    },

    /**
     * Persists the base currency metadata ({ Symbol, Rate }).
     */
    setBaseCurrency: async (data) => {
        await safeSet(BASE_CURRENCY, JSON.stringify(data));
    },
    /**
     * Reads the base currency metadata. Returns null if missing or parse fails.
     */
    getBaseCurrency: async () => {
        const value = await safeGet(BASE_CURRENCY);
        return safeParse(value);
    },

    /**
     * Persists the encrypted OTP login payload (the encrypted JSON of
     * `{ userID, storeDB }` written by UserLogin.vue.submit). Replaces the
     * raw `localStorage.setItem("users", ...)` bypass that was previously
     * throwing uncaught QuotaExceededError on web. Safe: routed through
     * safeSet so quota errors are silently swallowed.
     * @param {string} encryptedPayload AES-encrypted string from UserLogin.vue.
     */
    setUsers: async (encryptedPayload) => {
        await safeSet(USERS, encryptedPayload);
    },
    /**
     * Reads the encrypted OTP login payload written by setUsers. Returns null
     * if missing or read fails. The caller is responsible for AES decryption —
     * this layer only handles persistence.
     * @returns {Promise<string|null>}
     */
    getUsers: async () => {
        return await safeGet(USERS);
    },

    /**
     * Persists the support-agent username set after OTP verification.
     * Replaces the raw `localStorage.setItem("supportAgent", ...)` call in
     * OtpLogin.vue so writes flow through the unified Store (IndexedDB on web).
     * Safe: routed through safeSet.
     * @param {string} supportAgent The verified support-agent username.
     */
    setSupportAgent: async (supportAgent) => {
        await safeSet(SUPPORT_AGENT, supportAgent);
    },
    /**
     * Reads the persisted support-agent username. Returns null if missing or
     * read fails. The NavigationDrawer logout flow depends on this to swap
     * `Admin` for the real support-agent username when recording the logout.
     * @returns {Promise<string|null>}
     */
    getSupportAgent: async () => {
        return await safeGet(SUPPORT_AGENT);
    },
    /**
     * Removes the support-agent entry from storage. Used by logout flows.
     * Safe: routed through Store.remove and swallows quota/race errors.
     */
    removeSupportAgent: async () => {
        try {
            await Store.remove({ key: SUPPORT_AGENT });
        } catch (e) {
            console.warn('Storage.removeSupportAgent failed', e);
        }
    },
    /**
     * Removes the OTP login payload from storage. Called by OtpLogin.vue
     * after successful OTP verification to drop the temporary encrypted blob.
     * Safe: routed through Store.remove and swallows quota/race errors.
     */
    removeUsers: async () => {
        try {
            await Store.remove({ key: USERS });
        } catch (e) {
            console.warn('Storage.removeUsers failed', e);
        }
    },
}